import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tv } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/browse";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name || res.user.email}`);
      nav(next);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <Tv className="h-7 w-7 text-brand-primary" />
          <span className="font-display text-3xl"><span className="text-white">BALL0UT</span><span className="text-brand-primary neon-text">TV</span></span>
        </Link>

        <div className="border border-white/10 bg-brand-surface p-8 md:p-10">
          <h1 className="font-display text-3xl uppercase tracking-tight">Sign in</h1>
          <p className="text-brand-muted text-sm mt-2">Continue to your BallOutTV account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-brand-muted">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 bg-black border-white/10 text-white rounded-none h-12 focus-visible:ring-brand-primary focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-brand-muted">Password</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 bg-black border-white/10 text-white rounded-none h-12 focus-visible:ring-brand-primary focus-visible:ring-offset-0"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-brand-primary hover:bg-brand-primaryHover text-white rounded-none h-12 uppercase tracking-[0.15em] font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-brand-muted text-center">
            New to BallOutTV?{" "}
            <Link to="/register" className="text-brand-primary hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
